import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Baby, GraduationCap, Users as UsersIcon, Briefcase, Sunset, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { trackEvent } from "@/lib/analytics";
import LifeStageMixDonut from "@/components/LifeStageMixDonut";

/**
 * Phase 11 — interactive horizontal "Your Journey" timeline.
 * 5 life-stage nodes. Clicking a node reveals a card with that stage's
 * primary concerns + matched product tiles. Uses /api/life-stages data
 * passed in via props (so content is advised-by Payload/seed).
 */

const STAGE_META = {
  "childhood": { Icon: Baby, label: "Childhood", years: "0–18" },
  "young-adult": { Icon: GraduationCap, label: "Career Start", years: "18–30" },
  "mid-life": { Icon: UsersIcon, label: "Growing Family", years: "30–45" },
  "pre-retirement": { Icon: Briefcase, label: "Pre-Retirement", years: "45–60" },
  "retirement": { Icon: Sunset, label: "Legacy", years: "60+" },
};

export const JourneyTimeline = ({ stages = [], products = [] }) => {
  const [activeIdx, setActiveIdx] = useState(2); // start on "Growing Family"
  const safeStages = stages.length
    ? stages
    : Object.keys(STAGE_META).map((id) => ({
        id,
        name: STAGE_META[id].label,
        age_range: STAGE_META[id].years,
        primary_concerns: [],
        recommended_product_ids: [],
      }));

  const active = safeStages[activeIdx] || safeStages[0];
  const productsById = Object.fromEntries(products.map((p) => [p.id, p]));
  const matched = (active.recommended_product_ids || [])
    .slice(0, 4)
    .map((pid) => productsById[pid])
    .filter(Boolean);

  const onSelect = (idx, stageId) => {
    setActiveIdx(idx);
    trackEvent("journey_stage_click", { stage_id: stageId });
  };

  return (
    <section
      id="journey"
      data-testid="journey-section"
      className="py-20 sm:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="lw-eyebrow">Your Journey</span>
          <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-lw-navy leading-[1.1]">
            A plan that <span className="italic">grows with you.</span>
          </h2>
          <p className="mt-4 text-base leading-relaxed text-lw-muted">
            Tap any life stage below to see the products most relevant for
            that chapter — and the questions families typically ask us.
          </p>
        </div>

        {/* Timeline rail */}
        <div className="relative">
          <div
            aria-hidden="true"
            className="absolute left-0 right-0 top-[38px] h-px"
            style={{
              background:
                "linear-gradient(90deg, rgba(212,175,55,0.15) 0%, rgba(212,175,55,0.8) 20%, rgba(212,175,55,0.8) 80%, rgba(212,175,55,0.15) 100%)",
            }}
          />
          <ol className="grid grid-cols-5 gap-2 sm:gap-6 relative">
            {safeStages.slice(0, 5).map((stage, i) => {
              const meta = STAGE_META[stage.id] || STAGE_META.childhood;
              const Icon = meta.Icon;
              const isActive = i === activeIdx;
              return (
                <li key={stage.id} className="flex flex-col items-center text-center">
                  <button
                    data-testid={`journey-node-${stage.id}`}
                    onClick={() => onSelect(i, stage.id)}
                    aria-pressed={isActive}
                    className={`relative w-[72px] h-[72px] sm:w-[76px] sm:h-[76px] rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                      isActive
                        ? "bg-lw-navy border-lw-gold text-white arc-halo"
                        : "bg-white border-lw-navy/15 text-lw-navy hover:border-lw-gold/60 hover:bg-lw-navy/5"
                    }`}
                  >
                    <Icon size={26} strokeWidth={1.75} />
                  </button>
                  <div
                    className={`mt-4 font-serif-display text-sm sm:text-base leading-tight transition-colors ${
                      isActive ? "text-lw-navy" : "text-lw-navy/70"
                    }`}
                  >
                    {meta.label}
                  </div>
                  <div className="mt-1 font-sans-ui text-[0.65rem] sm:text-[0.7rem] tracking-[0.14em] uppercase text-lw-gold">
                    Ages {meta.years}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        {/* Active stage card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35, ease: [0.22, 0.61, 0.36, 1] }}
            data-testid={`journey-active-card-${active.id}`}
            className="relative mt-14 rounded-2xl overflow-hidden gradient-navy-indigo p-8 sm:p-10 text-white"
          >
            <div
              aria-hidden="true"
              className="absolute -top-20 -right-20 w-[300px] h-[300px] rounded-full"
              style={{
                background:
                  "radial-gradient(circle, rgba(212,175,55,0.22) 0%, rgba(212,175,55,0) 70%)",
              }}
            />
            <div className="relative grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-5">
                <span className="lw-eyebrow">Stage spotlight</span>
                <h3 className="mt-3 font-serif-display text-2xl sm:text-3xl leading-tight">
                  {STAGE_META[active.id]?.label || active.name}
                </h3>
                <p className="mt-2 text-sm text-[#FAF7F2]/70">
                  Ages {active.age_range}
                </p>
                <ul className="mt-6 space-y-2.5">
                  {(active.primary_concerns || []).slice(0, 4).map((c) => (
                    <li key={c} className="flex items-start gap-2.5 text-sm text-[#FAF7F2]/85">
                      <span className="mt-[7px] h-1.5 w-1.5 rounded-full bg-lw-gold flex-shrink-0" />
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:col-span-7">
                <div className="font-sans-ui text-[0.72rem] tracking-[0.22em] uppercase text-lw-gold">
                  Matched products
                </div>
                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                  {matched.length === 0 ? (
                    <p className="text-sm text-[#FAF7F2]/70">
                      Products for this stage are loading.
                    </p>
                  ) : (
                    matched.map((p) => (
                      <Link
                        key={p.id}
                        to={`/products/${p.id}`}
                        data-testid={`journey-product-${p.id}`}
                        className="group bg-white/5 hover:bg-white/10 border border-white/10 hover:border-lw-gold/50 rounded-xl px-4 py-3.5 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-serif-display text-base leading-tight text-white truncate">
                              {p.name}
                            </div>
                            <div className="mt-1 text-xs text-[#FAF7F2]/70 line-clamp-2 leading-relaxed">
                              {p.tagline}
                            </div>
                          </div>
                          <ArrowRight
                            size={16}
                            className="flex-shrink-0 text-lw-gold translate-y-1 group-hover:translate-x-1 transition-transform"
                          />
                        </div>
                      </Link>
                    ))
                  )}
                </div>

                {/* Phase 12.3 — per-stage coverage-mix donut */}
                <div className="mt-6 pt-5 border-t border-white/10">
                  <LifeStageMixDonut stageId={active.id} />
                </div>

                <div className="mt-6 flex gap-3 flex-wrap">
                  <Link
                    to="/tools/recommender"
                    data-testid="journey-cta-recommender"
                    className="btn-gold text-sm"
                    onClick={() =>
                      trackEvent("journey_cta_click", { target: "recommender", stage_id: active.id })
                    }
                  >
                    Get my personalized plan <ArrowRight size={14} />
                  </Link>
                  <Link
                    to="/apply"
                    data-testid="journey-cta-advisor"
                    className="btn-ghost-light text-sm"
                    onClick={() =>
                      trackEvent("journey_cta_click", { target: "apply", stage_id: active.id })
                    }
                  >
                    Talk to an advisor
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default JourneyTimeline;
